import { AppThunk } from '../../../index';
import { getUserDetailsFailed, getUserDetailsStarted, getUserDetailsSuccess } from '../reducer';
import { UserDocument } from '../../../../models/documents';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { logOutUser } from './logOutUser';
import { COLLECTION } from '../../../../firestore';

/** Błędy snapshotu, po których warto odświeżyć token i spróbować jeszcze raz (np. wolny start sesji). */
const FATAL_CODES = ['permission-denied', 'unauthenticated'];
/** Ile razy po takim błędzie ponawiamy subskrypcję, zanim uznamy porażkę za ostateczną. */
const USER_DETAILS_RETRIES = 1;
/** Zabezpieczenie przed wiecznym spinnerem w ProtectedRoute, gdy odświeżenie tokenu nie wraca. */
export const TOKEN_REFRESH_TIMEOUT_MS = 10_000;

class TokenRefreshTimeoutError extends Error {}

function refreshToken(user: firebase.User): Promise<string> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new TokenRefreshTimeoutError()),
      TOKEN_REFRESH_TIMEOUT_MS
    );
    user.getIdToken(true).then(
      (token) => {
        clearTimeout(timer);
        resolve(token);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

export const getUserDetails =
  (uid: string, retriesLeft = USER_DETAILS_RETRIES): AppThunk =>
  async (dispatch, getState, { db, firestoreSubscriptions, analytics, auth }) => {
    dispatch(getUserDetailsStarted());
    try {
      const ref = db.collection(COLLECTION.USERS).doc(uid);

      if (firestoreSubscriptions.userDetailsListener) {
        firestoreSubscriptions.userDetailsListener();
      }

      const idTokenResult = await auth().currentUser?.getIdTokenResult();
      const isImpersonated = idTokenResult?.claims?.impersonated || false;

      firestoreSubscriptions.userDetailsListener = ref.onSnapshot(
        (snapshot) => {
          const data = snapshot.data() as
            | (UserDocument & {
                createdAt: firebase.firestore.Timestamp;
                updatedAt: firebase.firestore.Timestamp;
              })
            | undefined;
          dispatch(
            getUserDetailsSuccess(
              data
                ? {
                    ...data,
                    createdAt: data.createdAt?.toDate(),
                    updatedAt: data.updatedAt?.toDate(),
                    systemRole: idTokenResult?.claims?.systemRole || null,
                    isImpersonated,
                  }
                : undefined
            )
          );
          if (data) {
            analytics.updateUserProperties(uid, {
              email: data?.email,
              name: data.firstName ? `${data.firstName} ${data.lastName ?? ''}` : undefined,
            });
          }
        },
        (error) => {
          const isFatal = FATAL_CODES.includes(error.code);
          const currentUser = auth().currentUser;
          // Zmiana użytkownika w trakcie subskrypcji: błąd dotyczy starej sesji, a stan w store
          // należy już do nowego użytkownika — nie ruszamy go i nie ponawiamy.
          if (currentUser && currentUser.uid !== uid) return;

          // Pierwsza porażka z odrzuconym tokenem to zwykle chwilowy wyścig: zostajemy w FETCHING
          // (ProtectedRoute nie przekierowuje), a FAILED ustawiamy dopiero przy ostatecznej porażce.
          const willRetry = isFatal && retriesLeft > 0 && currentUser !== null;
          if (!willRetry) {
            dispatch(getUserDetailsFailed());
          }
          if (!isFatal) return;

          if (!currentUser) {
            dispatch(logOutUser());

            return;
          }
          if (!willRetry) return;

          refreshToken(currentUser)
            .then(() => {
              // Odświeżenie tokenu trwa — użytkownik mógł się w tym czasie wylogować albo zmienić.
              if (auth().currentUser?.uid !== uid) return;
              dispatch(getUserDetails(uid, retriesLeft - 1));
            })
            .catch((refreshError) => {
              dispatch(getUserDetailsFailed());
              if (!(refreshError instanceof TokenRefreshTimeoutError)) {
                dispatch(logOutUser());
              }
            });
        }
      );
    } catch (_e) {
      dispatch(getUserDetailsFailed());
    }
  };
